import {
  NumbersBreakdown,
  ReviewsSummary,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Paper, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { useRef } from "react";

import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { WorkplaceHighlightPills } from "../Highlights/Pills";
import { useWorkplaceHighlights } from "../Highlights/useWorkplaceHighlights";
import { WorkplaceCommentList } from "./Comment/List";
import { WorkplaceReviewsFooter } from "./Footer";
import { PatientLoadSection } from "./PatientLoadSection";
import { RatingSummary } from "./RatingSummary";
import { SubCategoryRatings } from "./SubCategoryRatings";
import { useDelayedWorkplaceReviewFlag } from "./useDelayedWorkplaceReviewFlag";

export interface WorkplaceReviewsModalProps {
  modalState: UseModalState;
  workplaceId: string;
  workplaceType?: string;
}

export function WorkplaceReviewsModal(props: WorkplaceReviewsModalProps) {
  const { modalState, workplaceId, workplaceType } = props;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const showNewRatingSummary = useDelayedReviewUI || isReviewUXUpdateEnabled;
  const showGeneralRating = !isReviewUXUpdateEnabled;
  const showSubCategoryRatings = isReviewUXUpdateEnabled;
  const showPatientLoad = useDelayedReviewUI || isReviewUXUpdateEnabled;
  const showHighlights = !isReviewUXUpdateEnabled;

  const { data: ratingBreakdowns, isSuccess: ratingBreakdownsIsSuccess } = useGetRatingAggregates(
    String(workplaceId)
  );

  const { highlights, isLoading: isHighlightsLoading } = useWorkplaceHighlights({
    workplaceId,
    includePatientRatioHighlights: !useDelayedReviewUI,
    enabled: showHighlights,
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollRef);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <StickyScrollPageHeader
        headerVariant="tertiary"
        title="Workplace Reviews"
        scrollContainerRef={scrollRef}
        isLoading={false}
      />

      <Stack ref={scrollRef} sx={{ overflow: "auto", height: "100%" }}>
        {ratingBreakdownsIsSuccess && (
          <Paper variant="tertiary" sx={{ px: 7, paddingBottom: 10 }}>
            <Stack spacing={8}>
              {showNewRatingSummary ? (
                <RatingSummary
                  rating={ratingBreakdowns.avgRating}
                  ratingsCount={ratingBreakdowns.numberOfRatings}
                />
              ) : (
                <ReviewsSummary
                  rating={ratingBreakdowns.avgRating}
                  ratingsCount={ratingBreakdowns.numberOfRatings}
                />
              )}

              {showGeneralRating && ratingBreakdowns.numberOfRatings > 0 && (
                <>
                  <Text semibold variant="h6" sx={{ paddingTop: 3 }}>
                    General Rating
                  </Text>

                  <NumbersBreakdown
                    numberBreakdowns={ratingBreakdowns.ratingsBreakdown.map((rating) => ({
                      value: rating.rating,
                      count: rating.numberOfRatings,
                    }))}
                    defaultValues={[1, 2, 3, 4, 5]}
                  />
                </>
              )}

              {showSubCategoryRatings && <SubCategoryRatings workplaceId={workplaceId} />}

              {showPatientLoad && <PatientLoadSection workplaceId={workplaceId} />}

              {showHighlights && highlights.length > 0 && (
                <>
                  <Text semibold variant="h6" sx={{ paddingTop: 3 }}>
                    Highlights
                  </Text>

                  <WorkplaceHighlightPills
                    highlights={highlights}
                    isLoading={isHighlightsLoading}
                  />
                </>
              )}
            </Stack>
          </Paper>
        )}

        <Paper variant="secondary" sx={{ px: 5, py: 10, flexGrow: 1 }}>
          <WorkplaceCommentList workplaceId={workplaceId} />
        </Paper>
      </Stack>

      <WorkplaceReviewsFooter workplaceId={workplaceId} workplaceType={workplaceType} />
    </FullScreenDialog>
  );
}
